/**
 * MyWallet - 我的钱包 Tab
 * 展示余额、快速充值选项、支持微信/支付宝在线支付和人工客服充值
 */
import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Wallet,
  Zap,
  Gift,
  AlertCircle,
  X,
  Copy,
  Check,
  Loader2,
  Smartphone,
  CreditCard,
  User,
  CheckCircle,
} from 'lucide-react';
import Button from '../../../components/ui/Button';
import { QRCodeSVG } from 'qrcode.react';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface RechargeOption {
  amount: number;
  bonus: number;
  popular: boolean;
}

interface PaymentMethod {
  id: string;
  name: string;
  icon: string;
}

interface QrCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  codeUrl: string;
  amount: number;
  orderNo: string;
  onSuccess: () => void;
}

function QrCodeModal({ isOpen, onClose, codeUrl, amount, orderNo, onSuccess }: QrCodeModalProps) {
  const [status, setStatus] = useState<'pending' | 'paid' | 'failed'>('pending');
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isOpen || !orderNo) return;
    pollingRef.current = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/payment/order/${orderNo}/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const result = await response.json();
        if (result.success) {
          if (result.data.status === 'paid') {
            setStatus('paid');
            if (pollingRef.current) clearInterval(pollingRef.current);
            setTimeout(() => onSuccess(), 1500);
          } else if (result.data.status === 'failed') {
            setStatus('failed');
            if (pollingRef.current) clearInterval(pollingRef.current);
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 3000);
    setTimeout(() => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    }, 5 * 60 * 1000);
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [isOpen, orderNo, onSuccess]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-sm border border-slate-200 w-full max-w-md overflow-hidden"
      >
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white relative">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-2xl font-bold mb-2">微信支付</h2>
          <p className="opacity-90">充值金额 ¥{amount}</p>
        </div>
        <div className="p-8 text-center">
          <AnimatePresence mode="wait">
            {status === 'pending' && (
              <motion.div key="pending" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="bg-gray-50 p-6 rounded-2xl inline-block mb-6">
                  <QRCodeSVG value={codeUrl} size={200} />
                </div>
                <p className="text-gray-600 mb-2">请使用微信扫一扫完成支付</p>
                <p className="text-sm text-gray-400">支付完成后将自动充值到账</p>
                <div className="mt-6 flex items-center justify-center gap-2 text-gray-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">等待支付...</span>
                </div>
              </motion.div>
            )}
            {status === 'paid' && (
              <motion.div key="paid" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} className="py-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-green-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">支付成功！</h3>
                <p className="text-gray-500">¥{amount} 已充值到您的账户</p>
              </motion.div>
            )}
            {status === 'failed' && (
              <motion.div key="failed" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-8">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-10 h-10 text-red-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">支付失败</h3>
                <p className="text-gray-500 mb-4">请稍后重试或联系客服</p>
                <button onClick={onClose} className="px-6 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors">
                  关闭
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}

export default function MyWallet() {
  const { user, refreshUser } = useAuth();
  const [rechargeOptions, setRechargeOptions] = useState<RechargeOption[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('manual');
  const [customerService, setCustomerService] = useState<{ wechatId?: string; qrCodeUrl?: string }>({});
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 在线支付弹窗状态
  const [showQrModal, setShowQrModal] = useState(false);
  const [codeUrl, setCodeUrl] = useState('');
  const [orderNo, setOrderNo] = useState('');
  const [pendingAmount, setPendingAmount] = useState(0);

  useEffect(() => {
    // 获取充值配置
    fetch(`${API_BASE_URL}/config/recharge`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.data) && data.data.length > 0) {
          setRechargeOptions(data.data);
        } else {
          // 使用默认配置
          setRechargeOptions([
            { amount: 29, bonus: 2, popular: false },
            { amount: 99, bonus: 15, popular: true },
            { amount: 299, bonus: 60, popular: false },
          ]);
        }
      })
      .catch(() => {
        setRechargeOptions([
          { amount: 29, bonus: 2, popular: false },
          { amount: 99, bonus: 15, popular: true },
          { amount: 299, bonus: 60, popular: false },
        ]);
      });

    // 获取可用支付方式
    fetch(`${API_BASE_URL}/payment/methods`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.data)) {
          const methods: PaymentMethod[] = data.data.map((c: any) => {
            if (c.provider === 'wechat') return { id: 'wechat', name: '微信支付', icon: '💚' };
            if (c.provider === 'alipay') return { id: 'alipay', name: '支付宝', icon: '💙' };
            return null;
          }).filter(Boolean);
          // 始终添加人工充值
          methods.push({ id: 'manual', name: '联系客服', icon: '👤' });
          setPaymentMethods(methods);
          if (methods.length > 0) {
            setSelectedMethod(methods[methods.length - 1].id);
          }
        } else {
          setPaymentMethods([{ id: 'manual', name: '联系客服', icon: '👤' }]);
        }
      })
      .catch(() => {
        setPaymentMethods([{ id: 'manual', name: '联系客服', icon: '👤' }]);
      });

    // 获取客服信息
    fetch(`${API_BASE_URL}/config/customer-service`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setCustomerService(data.data || {});
        }
      })
      .catch((err) => {
        console.error('获取客服信息失败:', err);
      });
  }, []);

  const handleSelectAmount = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount('');
    setError('');
  };

  const handleCustomAmount = (value: string) => {
    if (!/^\d*\.?\d{0,2}$/.test(value)) return;
    setCustomAmount(value);
    const num = parseFloat(value);
    if (!isNaN(num) && num > 0) {
      setSelectedAmount(num);
    } else {
      setSelectedAmount(null);
    }
    setError('');
  };

  const createPaymentOrder = async (amount: number, channel: 'alipay' | 'wechat') => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/payment/create-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ amount, channel }),
    });
    return await response.json();
  };

  const handleRecharge = async () => {
    if (!selectedAmount || selectedAmount <= 0) {
      setError('请选择或输入充值金额');
      return;
    }

    setError('');

    if (selectedMethod === 'manual') {
      setShowModal(true);
      return;
    }

    try {
      setLoading(true);
      const channel = selectedMethod as 'alipay' | 'wechat';
      const result = await createPaymentOrder(selectedAmount, channel);

      if (!result.success) {
        setError(result.error || '创建订单失败');
        return;
      }

      const { orderNo: newOrderNo, type, url, codeUrl: qrCodeUrl } = result.data;
      setOrderNo(newOrderNo);
      setPendingAmount(selectedAmount);

      if (channel === 'wechat') {
        if (type === 'qrcode') {
          setCodeUrl(qrCodeUrl);
          setShowQrModal(true);
        } else if (type === 'h5') {
          window.location.href = url;
        }
      } else {
        // 支付宝
        window.open(url, '_blank');
        const checkStatus = setInterval(async () => {
          const statusRes = await fetch(`${API_BASE_URL}/payment/order/${newOrderNo}/status`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          const statusData = await statusRes.json();
          if (statusData.success && statusData.data.status === 'paid') {
            clearInterval(checkStatus);
            await refreshUser();
          }
        }, 3000);
        setTimeout(() => clearInterval(checkStatus), 5 * 60 * 1000);
      }
    } catch (err) {
      setError('支付请求失败');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async () => {
    setShowQrModal(false);
    await refreshUser();
    setOrderNo('');
    setCodeUrl('');
    setPendingAmount(0);
  };

  const copyWechatId = () => {
    if (customerService.wechatId) {
      navigator.clipboard.writeText(customerService.wechatId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getMethodIcon = (id: string) => {
    if (id === 'wechat') return <Smartphone className="w-5 h-5 text-green-600" />;
    if (id === 'alipay') return <CreditCard className="w-5 h-5 text-blue-600" />;
    return <User className="w-5 h-5 text-slate-600" />;
  };

  return (
    <div className="flex flex-col gap-6">
      {/* 错误提示 */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-3 bg-red-50 text-red-700 rounded-xl border border-red-100 px-4 py-3"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 余额大数字显示 */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <div className="flex items-center gap-2 text-slate-600 mb-2">
          <Wallet className="w-5 h-5" />
          <span>账户余额</span>
        </div>
        <div className="text-5xl font-bold text-slate-800 mb-4">
          ¥{user?.balance?.toFixed(2) || '0.00'}
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Gift className="w-4 h-4" />
          <span>注册即送 ¥10 体验金，可创作 2-3 本绘本</span>
        </div>
      </div>

      {/* 快速充值 */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <div className="flex items-center gap-2 text-slate-800 mb-4">
          <Zap className="w-5 h-5 text-amber-500" />
          <h3 className="text-lg font-bold">快速充值</h3>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {rechargeOptions.map((option) => (
            <motion.button
              key={option.amount}
              onClick={() => handleSelectAmount(option.amount)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`relative rounded-xl border-2 p-4 transition-all ${
                selectedAmount === option.amount && !customAmount
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-slate-200 hover:border-indigo-200'
              }`}
            >
              {option.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-500 text-white text-xs font-bold rounded-full">
                  最受欢迎
                </div>
              )}

              <div className="text-3xl font-bold text-slate-800 mb-1">
                ¥{option.amount}
              </div>
              <div className="text-sm text-slate-500">
                送 <span className="text-amber-500 font-bold">¥{option.bonus}</span>
              </div>
              <div className="mt-2 text-xs text-slate-400">
                实得 ¥{option.amount + option.bonus}
              </div>
            </motion.button>
          ))}
        </div>

        {/* 自定义金额 */}
        <div className="mt-6 pt-6 border-t border-slate-100">
          <label className="block text-sm font-medium text-slate-700 mb-3">其他金额</label>
          <div className="relative max-w-xs">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-lg">¥</span>
            <input
              type="text"
              value={customAmount}
              onChange={(e) => handleCustomAmount(e.target.value)}
              placeholder="输入自定义金额"
              className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
            />
          </div>
        </div>
      </div>

      {/* 支付方式 */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <div className="flex items-center gap-2 text-slate-800 mb-4">
          <h3 className="text-lg font-bold">支付方式</h3>
        </div>

        <div className="flex flex-col gap-4">
          {paymentMethods.map((method) => (
            <button
              key={method.id}
              onClick={() => setSelectedMethod(method.id)}
              className={`w-full flex items-center gap-4 rounded-xl border-2 transition-all p-4 ${
                selectedMethod === method.id
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-slate-200 hover:border-indigo-200'
              }`}
            >
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                {getMethodIcon(method.id)}
              </div>
              <div className="flex-1 text-left">
                <div className="font-medium text-slate-800">{method.name}</div>
                {method.id === 'manual' && (
                  <div className="text-xs text-amber-600">当前可用</div>
                )}
              </div>
              {selectedMethod === method.id && (
                <div className="w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* 充值按钮 */}
      <Button
        size="lg"
        className="w-full bg-indigo-600 hover:bg-indigo-700"
        onClick={handleRecharge}
        disabled={loading}
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            创建订单中...
          </>
        ) : selectedAmount ? (
          `确认充值 ¥${selectedAmount}`
        ) : (
          '请选择充值金额'
        )}
      </Button>

      {/* 提示信息 */}
      <div className="flex items-start gap-3 bg-amber-50 rounded-xl border border-amber-100 p-4">
        <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-700">
          <p className="font-medium mb-1">充值说明</p>
          <p>
            人工充值请联系客服微信{' '}
            <span className="font-semibold">{customerService.wechatId || 'BadiBook2024'}</span>{' '}
            完成充值。
          </p>
          <p className="mt-1">在线支付金额将实时到账，如有问题请联系客服。</p>
        </div>
      </div>

      {/* 客服二维码弹层 */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="relative w-full max-w-sm bg-white rounded-2xl shadow-lg p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowModal(false)}
              className="absolute right-4 top-4 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center">
              <h4 className="text-lg font-bold text-slate-800 mb-2">联系客服完成充值</h4>
              <p className="text-sm text-slate-500 mb-5">请添加客服微信，发送充值金额完成人工充值</p>

              {customerService.qrCodeUrl ? (
                <div className="mb-5 flex justify-center">
                  <img
                    src={customerService.qrCodeUrl}
                    alt="客服微信二维码"
                    className="w-48 h-48 rounded-xl border border-slate-200 object-contain"
                  />
                </div>
              ) : null}

              <div className="inline-flex items-center gap-2 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
                <span className="text-sm text-slate-500">客服微信</span>
                <span className="text-base font-semibold text-slate-800">
                  {customerService.wechatId || 'BadiBook2024'}
                </span>
                <button
                  onClick={copyWechatId}
                  className="ml-2 text-indigo-600 hover:text-indigo-700 transition-colors"
                  title="复制微信号"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>

              <div className="mt-6">
                <Button size="md" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setShowModal(false)}>
                  我知道了
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 微信支付二维码弹窗 */}
      <AnimatePresence>
        {showQrModal && (
          <QrCodeModal
            isOpen={showQrModal}
            onClose={() => {
              setShowQrModal(false);
              setOrderNo('');
            }}
            codeUrl={codeUrl}
            amount={pendingAmount}
            orderNo={orderNo}
            onSuccess={handlePaymentSuccess}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
